import { useEffect } from "react";

const SITE_ORIGIN = "https://apex-innovate-kit.lovable.app";

type Props = {
  title: string;
  description: string;
  /** Path including leading slash (e.g. "/about"). Defaults to current pathname. */
  path?: string;
  /** Absolute URL for the social preview image. Optional. */
  image?: string;
};

/**
 * Sets per-page <title>, meta description, canonical and the og:* / twitter:*
 * tags that JS-executing crawlers (Googlebot) consume. Social-preview crawlers
 * (LinkedIn, Slack, FB) keep reading whatever ships in index.html.
 */
export function SEO({ title, description, path, image }: Props) {
  useEffect(() => {
    const pageTitle = `${title} — Smart Canvas`;
    document.title = pageTitle;

    const url =
      SITE_ORIGIN +
      (path ?? (typeof window !== "undefined" ? window.location.pathname : "/"));

    const upsertMeta = (selector: string, attr: "name" | "property", key: string, value: string) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[property="og:title"]', "property", "og:title", pageTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", pageTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    if (image) {
      upsertMeta('meta[property="og:image"]', "property", "og:image", image);
      upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image);
    }

    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
  }, [title, description, path, image]);
  return null;
}